function finiteNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
  ) {
    return null;
  }

  return Number(value);
}

const MAX_SIGNATURE_SPREAD_PERCENT = 0.5;
const MIN_SIGNATURE_DAILY_VOLUME = 500_000;
const MIN_SIGNATURE_DOLLAR_VOLUME = 5_000_000;
const MAX_SIGNATURE_CATALYST_AGE_MS = 72 * 60 * 60 * 1000;
const SIGNATURE_CATALYST_PATTERN =
  /\b(earnings|quarterly results?|revenue|eps|guidance|fda|approvals?|clinical[- ]trials?|contracts?|partnerships?|acquisitions?|mergers?|offerings?|financing|upgrades?|downgrades?|stock[- ]splits?|dividends?|buybacks?)\b/i;
export function findAuthenticatedRecentCatalyst(
  payload,
  symbol,
  now = Date.now()
) {
  const normalizedSymbol = String(symbol || "")
    .trim()
    .toUpperCase();
  const normalizedNow = now instanceof Date ? now.getTime() : Number(now);

  if (
    !normalizedSymbol ||
    !Number.isFinite(normalizedNow) ||
    payload?.symbol !== normalizedSymbol ||
    payload?.source?.provider !== "Alpaca" ||
    payload?.source?.source_endpoint !== "stock_news"
  ) {
    return null;
  }

  const articles = Array.isArray(payload?.news) ? payload.news : [];

  return (
    articles.find((article) => {
      const headline = String(article?.headline || "").trim();
      const summary = String(article?.summary || "").trim();
      const createdAt = Date.parse(article?.created_at || "");
      const articleSymbols = Array.isArray(article?.symbols)
        ? article.symbols.map((value) => String(value).toUpperCase())
        : [];
      const age = normalizedNow - createdAt;
      const catalystText = `${headline} ${summary}`;

      return (
        Boolean(headline) &&
        SIGNATURE_CATALYST_PATTERN.test(catalystText) &&
        articleSymbols.includes(normalizedSymbol) &&
        Number.isFinite(createdAt) &&
        age >= 0 &&
        age <= MAX_SIGNATURE_CATALYST_AGE_MS
      );
    }) || null
  );
}
export function applySignatureProductionProtection(
  analysis,
  opportunity = {}
) {
  const hasUsableAuthenticatedQuote =
    Number.isFinite(opportunity.bid) &&
    opportunity.bid > 0 &&
    Number.isFinite(opportunity.ask) &&
    opportunity.ask >= opportunity.bid &&
    Boolean(opportunity.quoteUpdatedAt);

  if (analysis?.status !== "GREEN") {
    return analysis;
  }

  if (!hasUsableAuthenticatedQuote) {
    return {
      ...analysis,
      status: "YELLOW",
      reason: "Authenticated bid/ask quote is unavailable",
    };
  }

  const midpoint = (opportunity.bid + opportunity.ask) / 2;
  const spreadPercent =
    ((opportunity.ask - opportunity.bid) / midpoint) * 100;

  if (spreadPercent > MAX_SIGNATURE_SPREAD_PERCENT) {
    return {
      ...analysis,
      status: "YELLOW",
      reason: `Bid/ask spread ${spreadPercent.toFixed(
        2
      )}% exceeds the 0.50% maximum`,
    };
  }
  const hasUsableAuthenticatedLiquidity =
    Number.isFinite(opportunity.price) &&
    opportunity.price > 0 &&
    Number.isFinite(opportunity.volume) &&
    opportunity.volume >= 0;

  if (!hasUsableAuthenticatedLiquidity) {
    return {
      ...analysis,
      status: "YELLOW",
      reason: "Authenticated price/volume liquidity data is unavailable",
    };
  }
    if (opportunity.volume < MIN_SIGNATURE_DAILY_VOLUME) {
    return {
      ...analysis,
      status: "YELLOW",
      reason: "Daily volume is below the 500,000-share minimum",
    };
  }

  const dollarVolume = opportunity.price * opportunity.volume;

  if (dollarVolume < MIN_SIGNATURE_DOLLAR_VOLUME) {
    return {
      ...analysis,
      status: "YELLOW",
      reason:
        "Estimated daily dollar volume is below the $5,000,000 minimum",
    };
  }
    if (!opportunity.catalyst) {
    return {
      ...analysis,
      status: "YELLOW",
      reason:
        "No authenticated symbol-specific catalyst found within the last 72 hours",
    };
  }

  return analysis;
}

export function calculateEma(values, period) {
  const normalizedPeriod = Number(period);
  const numbers = values.map(finiteNumber);

  if (
    !Number.isInteger(normalizedPeriod) ||
    normalizedPeriod <= 0 ||
    numbers.some((value) => value === null) ||
    numbers.length < normalizedPeriod
  ) {
    return Array(values.length).fill(null);
  }

  const result = Array(numbers.length).fill(null);
  const initialAverage =
    numbers
      .slice(0, normalizedPeriod)
      .reduce((sum, value) => sum + value, 0) / normalizedPeriod;

  const multiplier = 2 / (normalizedPeriod + 1);
  result[normalizedPeriod - 1] = initialAverage;

  for (let index = normalizedPeriod; index < numbers.length; index += 1) {
    result[index] =
      numbers[index] * multiplier +
      result[index - 1] * (1 - multiplier);
  }

  return result;
}

export function calculateMacd(values) {
  const ema12 = calculateEma(values, 12);
  const ema26 = calculateEma(values, 26);

  const macdLine = values.map((_, index) => {
    if (ema12[index] === null || ema26[index] === null) return null;
    return ema12[index] - ema26[index];
  });

  const compactMacd = macdLine.filter((value) => value !== null);
  const compactSignal = calculateEma(compactMacd, 9);
  let compactIndex = 0;

  const signalLine = macdLine.map((value) => {
    if (value === null) return null;

    const signal = compactSignal[compactIndex] ?? null;
    compactIndex += 1;
    return signal;
  });

  const histogram = macdLine.map((value, index) => {
    if (value === null || signalLine[index] === null) return null;
    return value - signalLine[index];
  });

  return {
    macdLine,
    signalLine,
    histogram,
  };
}

export function analyzeSignatureBars(payload) {
  const bars = Array.isArray(payload?.bars) ? payload.bars : [];

  const normalizedBars = bars
    .map((bar) => ({
      open: finiteNumber(bar?.o),
      close: finiteNumber(bar?.c),
      volume: finiteNumber(bar?.v),
      timestamp: bar?.t || null,
    }))
    .filter((bar) => bar.close !== null);

  if (normalizedBars.length < 36) {
    return {
      status: "YELLOW",
      reason: "Insufficient authenticated candle history",
      ema20: null,
      macd: null,
      signal: null,
      relativeVolume: null,
      updatedAt: normalizedBars.at(-1)?.timestamp || null,
      source: payload?.source || null,
    };
  }

  const closes = normalizedBars.map((bar) => bar.close);
  const ema20Series = calculateEma(closes, 20);
  const { macdLine, signalLine } = calculateMacd(closes);

  const latestIndex = normalizedBars.length - 1;
  const priorIndex = latestIndex - 1;
  const crossoverBaseIndex = latestIndex - 2;

  const latestBar = normalizedBars[latestIndex];
  const ema20 = ema20Series[latestIndex];
  const macd = macdLine[latestIndex];
  const signal = signalLine[latestIndex];

  const priorMacd = macdLine[priorIndex];
  const priorSignal = signalLine[priorIndex];
  const crossoverBaseMacd = macdLine[crossoverBaseIndex];
  const crossoverBaseSignal = signalLine[crossoverBaseIndex];

  const previousVolumes = normalizedBars
    .slice(Math.max(0, latestIndex - 20), latestIndex)
    .map((bar) => bar.volume)
    .filter((volume) => volume !== null);

  const averageVolume =
    previousVolumes.length > 0
      ? previousVolumes.reduce((sum, volume) => sum + volume, 0) /
        previousVolumes.length
      : null;

  const relativeVolume =
    latestBar.volume !== null &&
    averageVolume !== null &&
    averageVolume > 0
      ? latestBar.volume / averageVolume
      : null;

  const latestGreenCandle =
    latestBar.open !== null && latestBar.close > latestBar.open;
  const aboveEma20 = ema20 !== null && latestBar.close > ema20;
  const macdBullish =
    macd !== null && signal !== null && macd > signal;

  const priorBullishCrossover =
    priorMacd !== null &&
    priorSignal !== null &&
    crossoverBaseMacd !== null &&
    crossoverBaseSignal !== null &&
    crossoverBaseMacd <= crossoverBaseSignal &&
    priorMacd > priorSignal;

  const confirmed =
    priorBullishCrossover &&
    latestGreenCandle &&
    aboveEma20 &&
    macdBullish &&
    relativeVolume !== null &&
    relativeVolume >= 1;

  const bearish =
    ema20 !== null &&
    macd !== null &&
    signal !== null &&
    latestBar.close < ema20 &&
    macd < signal;

  return {
    status: confirmed ? "GREEN" : bearish ? "RED" : "YELLOW",
    reason: confirmed
      ? "MACD crossover confirmed by a green candle, EMA 20, and volume"
      : bearish
        ? "Price is below EMA 20 with bearish MACD"
        : "Authenticated setup is waiting for full confirmation",
    ema20,
    macd,
    signal,
    relativeVolume,
    updatedAt: latestBar.timestamp,
    source: payload?.source || null,
  };
}